import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import {
  CurrentAccountInfo,
  GlobalNav,
  SIDEBAR_TYPE,
  SideNavLink,
  ToolbarItem,
} from '@amzn/aac-chrome-internal-api';
import type { RootState } from 'src/app/store';
import { ImageProps } from 'src/commons/utils/types';
import { AjaxInfo, UserInfo } from '../../header/store/slice';
import { DesktopNavPublisher } from 'src/commons/utils/metrics';
import { Flag, FlagType } from 'src/app/slice';
import { showInternalView } from 'src/commons/utils/helpers';

// Define a type for the slice state
export interface NavState extends GlobalNav {
  userInfo: UserInfo;
  currentAccountInfo: CurrentAccountInfo;
  ajaxInfo: AjaxInfo;
  activeCategory: string;
  accountSwitcherLabel: string;
  menuText: string;
  backupToolbarItems: ToolbarItem[];
  toolbarItemsWithoutInternalApps: Array<ToolbarItemProps>;
  landingAppName: string;
}

export type ToolbarItemProps = ToolbarItem;

export interface SideNavLinkProps extends SideNavLink {
  isInternal?: boolean;
}

export interface SelectedToolbarItem {
  iconPath: string;
  category: string;
  url: string;
  appName: string;
  appKeyName: string;
  isInternal?: boolean;
  shouldDisplayCategory: boolean;
  shouldDisplayExternalToggle: boolean;
}

// Define the initial state using that type
export const navInitialState: NavState = {
  logoImage: {
    src: '',
    alt: '',
  },
  internalLogoImage: {
    src: '',
    alt: '',
  },
  toolbarItems: [],
  backupToolbarItems: [],
  activeCategory: '',
  userInfo: {
    customerInfo: {
      fullName: '',
    },
    signOutInfo: {
      label: '',
      url: '',
    },
    languageInfo: {
      currentLocale: '',
      label: '',
      languageOptions: [],
      changeLanguageText: '',
    },
    notificationSettingsInfo: {
      url: '',
      label: '',
    },
    userSettingsInfo: {
      url: '',
      label: '',
    },
  },
  currentAccountInfo: {
    currentAccountName: '',
    currentMarketplace: '',
    currentMarketplaceId: '',
  } as CurrentAccountInfo,
  ajaxInfo: {
    endpoint: '',
  },
  accountSwitcherLabel: '',
  menuText: '',
  toolbarItemsWithoutInternalApps: [],
  landingAppName: '',
};

export const navSlice = createSlice({
  name: 'nav',
  // `createSlice` will infer the state type from the `initialState` argument
  initialState: navInitialState,
  reducers: {
    initInternalNavState: (state, action: PayloadAction<NavState>) => {
      return {
        ...state,
        ...action.payload,
        toolbarItemsWithoutInternalApps:
          getToolbarItemsWithoutInternalApps(action),
      };
    },
    initInternalNavStateWithBackupToolbar: (
      state,
      action: PayloadAction<NavState>,
    ) => {
      return {
        ...state,
        ...action.payload,
        toolbarItems: [],
        toolbarItemsWithoutInternalApps: [],
        backupToolbarItems: [...action.payload.toolbarItems],
      };
    },
    initAjaxNavState: (
      state,
      action: PayloadAction<GlobalNav | Record<string, unknown>>,
    ) => {
      return {
        ...state,
        ...action.payload,
        toolbarItemsWithoutInternalApps:
          getToolbarItemsWithoutInternalApps(action),
      };
    },
    updateActiveCategory: (state, action) => {
      if (action.payload == '') {
        DesktopNavPublisher.publishCounterMonitor('Close', 1);
      } else {
        DesktopNavPublisher.publishCounterMonitor(`Open-${action.payload}`, 1);
      }
      state.activeCategory = action.payload;
    },
    updateLandingAppName: (state, action) => {
      state.landingAppName = action.payload;
    },
    updateToolbarItemsWithBackup: (state) => {
      if (state.toolbarItems.length === 0) {
        state.toolbarItems = state.backupToolbarItems;
        state.toolbarItemsWithoutInternalApps =
          getToolbarItemsWithoutInternalApps({
            payload: { toolbarItems: state.toolbarItems },
          });
      }
    },
    updateSelectedNavItem: (state, action) => {
      const { toolbarItemId, linkId } = action.payload;
      // TODO: consider remove 'toolbarItemsWithoutInternalApps' prop and derive it from 'toolbarItems' directly
      // otherwise we will always need to handle both props which is duplicate and easy to miss
      const toolbarItems =
        state.toolbarItems.length > 0
          ? [...state.toolbarItems, ...state.toolbarItemsWithoutInternalApps]
          : [];
      for (const toolbarItem of toolbarItems) {
        if (!toolbarItem.sidebarGroups) continue;
        for (const group of toolbarItem.sidebarGroups) {
          for (const link of group.links) {
            link.isSelected =
              toolbarItemId === toolbarItem.id && link.id === linkId;
          }
        }
      }
    },
    updateSelectedApplication: (state, action) => {
      const { selectedApplication } = action.payload;
      const toolbarItems =
        state.toolbarItems.length > 0
          ? [...state.toolbarItems, ...state.toolbarItemsWithoutInternalApps]
          : [];
      for (const toolbarItem of toolbarItems) {
        if (!toolbarItem.sidebarGroups) continue;
        for (const group of toolbarItem.sidebarGroups) {
          for (const link of group.links) {
            link.isSelected = selectedApplication === link.id;
          }
        }
      }
    },
  },
});

export const {
  initInternalNavState,
  initInternalNavStateWithBackupToolbar,
  initAjaxNavState,
  updateActiveCategory,
  updateLandingAppName,
  updateToolbarItemsWithBackup,
  updateSelectedNavItem,
  updateSelectedApplication,
} = navSlice.actions;

// Other code such as selectors can use the imported `RootState` type
export const selectLogoImage = (state: RootState): ImageProps =>
  state.nav.logoImage;
export const selectInternalLogoImage = (
  state: RootState,
): ImageProps | undefined => state.nav.internalLogoImage;
export const selectToolbarItems = (
  state: RootState,
): Array<ToolbarItemProps> => {
  return showInternalView(
    state.shared.chromeConfig?.pageLayoutType,
    state.shared.internalChromeContext?.isInternalView,
  )
    ? state.nav.toolbarItems
    : state.nav.toolbarItemsWithoutInternalApps;
};
export const selectUserInfoFromNav = (state: RootState): UserInfo =>
  state.nav.userInfo;
export const selectActiveCategory = (state: RootState): string =>
  state.nav.activeCategory;
export const selectLandingAppName = (state: RootState): string =>
  state.nav.landingAppName;
export const selectRenderTagInPageName = (state: RootState): Flag | null => {
  const selectedLink = selectSelectedLink(state);
  if (selectedLink?.isBeta) {
    return {
      type: 'beta',
      label: 'Beta',
      description: 'Beta',
    };
  }
  if (selectedLink?.isNew) {
    return {
      type: 'new',
      label: 'New',
      description: 'New',
    };
  } else if (selectedLink?.calloutTag) {
    const flagType: FlagType =
      selectedLink.calloutTag?.toLowerCase() as FlagType;
    const label =
      flagType.charAt(0).toUpperCase() + flagType.slice(1).toLowerCase();
    return {
      type: flagType,
      label: label,
      description: label,
    };
  }
  return null;
};

export const selectSelectedToolbarItem = (
  state: RootState,
): SelectedToolbarItem | undefined => {
  const toolbarItems = state.nav.toolbarItems ?? [];
  for (const toolbarItem of toolbarItems) {
    if (!toolbarItem.sidebarGroups) continue;
    const links = toolbarItem.sidebarGroups.flatMap((group) => group.links);
    for (const link of links) {
      if (link.isSelected) {
        let shouldDisplayCategory = false;
        if (links.length > 1 || toolbarItem.sidebarGroups.length > 1) {
          shouldDisplayCategory = true;
        }

        return {
          iconPath: toolbarItem.iconPath,
          category: toolbarItem.toolTip,
          isInternal: link.isInternal,
          url: link.link.url,
          appName: link.link.label,
          shouldDisplayCategory: shouldDisplayCategory,
          shouldDisplayExternalToggle: !link.isInternal,
          appKeyName: link.appName ?? '',
        };
      }
    }
  }

  return undefined;
};

export const selectSelectedLink = (
  state: RootState,
): SideNavLinkProps | undefined => {
  const toolbarItems =
    state.nav.toolbarItems.length > 0 ? state.nav.toolbarItems : [];

  return toolbarItems
    .filter((item) => item.sidebarType !== SIDEBAR_TYPE.DIVIDER)
    .flatMap((item) => item.sidebarGroups)
    .flatMap((group) => group.links)
    .find((link) => link.isSelected);
};

export const getToolbarItemsWithoutInternalApps = ({
  payload,
}): ToolbarItem[] => {
  if (
    !payload ||
    !payload['toolbarItems'] ||
    payload['toolbarItems'].length === 0
  ) {
    return [];
  }
  // Deep cloning to avoid updating the actual payload.
  // eslint-disable-next-line compat/compat
  const toolbar = structuredClone(payload);

  return toolbar['toolbarItems'].filter((item) => {
    if (
      [SIDEBAR_TYPE.DIVIDER, SIDEBAR_TYPE.ALL_TOOLS].includes(item.sidebarType)
    )
      return true;
    const sidebarGroups = item.sidebarGroups?.filter((sidebarGroup) => {
      const links = sidebarGroup.links.filter(
        (sidebarLink) => !sidebarLink.isInternal,
      );
      return links.length > 0;
    });
    // remove internal apps from sidebar group when both internal and external apps are available under the same category
    item.sidebarGroups = sidebarGroups;
    return sidebarGroups?.length > 0;
  });
};

export default navSlice.reducer;
